import { useEffect, useState } from "react";

import { fetchProfile, ProfileApiError, savePassword, saveProfile } from "./api";
import type { ProfileResponse } from "./types";

type IdentityUpdate = {
  username: string;
  display_name: string;
  avatar_url: string | null;
};

function FieldError({ errors, name }: { errors: Record<string, string[]>; name: string }) {
  const messages = errors[name] ?? [];
  return messages.map((message) => <small className="profile-field-error" role="alert" key={message}>{message}</small>);
}

export function ProfilePage({ onIdentityUpdated }: { onIdentityUpdated: (update: IdentityUpdate) => void }) {
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [form, setForm] = useState({ username: "", first_name: "", last_name: "", email: "", avatar_id: null as number | null });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState({ current_password: "", new_password: "", confirmation: "" });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string[]>>({});

  const resetForm = (next: ProfileResponse) => {
    setForm({
      username: next.username,
      first_name: next.first_name,
      last_name: next.last_name,
      email: next.email,
      avatar_id: next.avatar_id,
    });
    setErrors({});
  };

  useEffect(() => {
    void fetchProfile()
      .then((next) => {
        setProfile(next);
        resetForm(next);
      })
      .catch((reason) => setNotice(reason instanceof Error ? reason.message : "Le profil n’a pas pu être chargé."))
      .finally(() => setLoading(false));
  }, []);

  const submitProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    setNotice(null);
    try {
      const next = await saveProfile(form);
      setProfile(next);
      resetForm(next);
      onIdentityUpdated({ username: next.username, display_name: next.display_name, avatar_url: next.avatar_url });
      setNotice("Votre profil a été enregistré.");
    } catch (reason) {
      if (reason instanceof ProfileApiError) setErrors(reason.fields);
      setNotice(reason instanceof Error ? reason.message : "Le profil n’a pas pu être enregistré.");
    } finally {
      setSaving(false);
    }
  };

  const submitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setPasswordErrors({});
    setNotice(null);
    try {
      await savePassword(password);
      setPassword({ current_password: "", new_password: "", confirmation: "" });
      setNotice("Votre mot de passe a été modifié. Votre session reste ouverte.");
    } catch (reason) {
      if (reason instanceof ProfileApiError) setPasswordErrors(reason.fields);
      setNotice(reason instanceof Error ? reason.message : "Le mot de passe n’a pas pu être modifié.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <section className="profile-page"><p>Chargement de votre profil…</p></section>;
  if (profile === null) return <section className="profile-page"><h1>Mon profil</h1><p role="alert">{notice}</p></section>;
  const identityEditable = profile.auth.identity_editable;

  return (
    <section className="profile-page">
      <header className="profile-heading">
        <div>
          <p className="eyebrow">Compte Cogito</p>
          <h1>Mon profil</h1>
          <p>Personnalisez votre identité visible et consultez vos informations scolaires.</p>
        </div>
        <div className="profile-preview" aria-label={`Aperçu de l’avatar de ${profile.display_name}`}>
          {profile.avatar_url ? <img src={profile.avatar_url} alt="" /> : <span aria-hidden="true">{profile.display_name.slice(0, 1).toUpperCase()}</span>}
          <strong>{profile.display_name}</strong>
        </div>
      </header>

      {notice && <p className="profile-notice" role="status">{notice}</p>}
      {profile.auth.managed_message && <p className="profile-managed">{profile.auth.managed_message}</p>}

      <div className="profile-layout">
        <form className="profile-card profile-form" onSubmit={(event) => void submitProfile(event)}>
          <h2>Informations personnelles</h2>
          <div className="profile-form-grid">
            <label>Pseudo<input value={form.username} disabled={!identityEditable} autoComplete="username" onChange={(event) => setForm({ ...form, username: event.target.value })} /><FieldError errors={errors} name="username" /></label>
            <label>Adresse électronique<input type="email" value={form.email} disabled={!identityEditable} autoComplete="email" onChange={(event) => setForm({ ...form, email: event.target.value })} /><FieldError errors={errors} name="email" /></label>
            <label>Prénom<input value={form.first_name} disabled={!identityEditable} autoComplete="given-name" onChange={(event) => setForm({ ...form, first_name: event.target.value })} /><FieldError errors={errors} name="first_name" /></label>
            <label>Nom<input value={form.last_name} disabled={!identityEditable} autoComplete="family-name" onChange={(event) => setForm({ ...form, last_name: event.target.value })} /><FieldError errors={errors} name="last_name" /></label>
          </div>

          <fieldset className="avatar-picker">
            <legend>Avatar</legend>
            {profile.avatars.length ? <div className="avatar-options">{profile.avatars.map((avatar) => (
              <label className={form.avatar_id === avatar.id ? "selected" : ""} key={avatar.id}>
                <input type="radio" name="avatar" value={avatar.id} checked={form.avatar_id === avatar.id} onChange={() => setForm({ ...form, avatar_id: avatar.id })} />
                <img src={avatar.image_url} alt={avatar.label} />
              </label>
            ))}</div> : <p>Aucun avatar n’est encore disponible.</p>}
            <FieldError errors={errors} name="avatar" />
          </fieldset>

          <div className="profile-actions">
            <button className="button button-primary" type="submit" disabled={saving}>Enregistrer</button>
            <button className="button profile-cancel" type="button" disabled={saving} onClick={() => resetForm(profile)}>Annuler</button>
          </div>
        </form>

        <aside className="profile-card profile-school">
          <h2>Cadre scolaire</h2>
          <dl>
            <div><dt>Niveau</dt><dd>{profile.school_level?.name ?? "Non renseigné"}</dd></div>
            <div><dt>Degré</dt><dd>{profile.school_degree?.name ?? "Non renseigné"}</dd></div>
            <div><dt>Établissements</dt><dd>{profile.schools.map((item) => item.name).join(", ") || "Non renseigné"}</dd></div>
            <div><dt>Matières</dt><dd>{profile.subjects.map((item) => item.name).join(", ") || "Non renseigné"}</dd></div>
            <div><dt>Rôles</dt><dd>{profile.roles.map((role) => role.label).join(", ")}</dd></div>
            <div><dt>Authentification</dt><dd>{profile.auth.mode === "local" ? "Compte Cogito" : "Compte institutionnel"}</dd></div>
          </dl>
          <p>Ces informations sont administrées par votre établissement.</p>
        </aside>
      </div>

      {profile.auth.password_change_enabled && <form className="profile-card password-form" onSubmit={(event) => void submitPassword(event)}>
        <h2>Changer mon mot de passe</h2>
        <div className="profile-form-grid">
          <label>Mot de passe actuel<input type="password" value={password.current_password} autoComplete="current-password" onChange={(event) => setPassword({ ...password, current_password: event.target.value })} /><FieldError errors={passwordErrors} name="current_password" /></label>
          <label>Nouveau mot de passe<input type="password" value={password.new_password} autoComplete="new-password" onChange={(event) => setPassword({ ...password, new_password: event.target.value })} /><FieldError errors={passwordErrors} name="new_password" /></label>
          <label>Confirmer le mot de passe<input type="password" value={password.confirmation} autoComplete="new-password" onChange={(event) => setPassword({ ...password, confirmation: event.target.value })} /><FieldError errors={passwordErrors} name="confirmation" /></label>
        </div>
        <button className="button button-primary" type="submit" disabled={saving}>Modifier le mot de passe</button>
      </form>}
    </section>
  );
}
